import { dec, formatQuantity, ZERO } from "@bl/domain";
import { Link } from "@tanstack/react-router";
import { ChevronDown, ChevronUp } from "lucide-react";
import type { ReactNode } from "react";
import type { Item, Location } from "../api";
import { CategoryChip, catStyle } from "../components/ui";
import {
  categoryLabel,
  formLabel,
  makerText,
  regionText,
  relative,
  showAvailable,
  showBalance,
  showDate,
  showOnHand,
  showQuantity,
  showSpec,
  showUnitCost,
} from "../format";
import { useLayout } from "../layout/LayoutProvider";
import type { Layout } from "../layout/layout";
import { type ShortFrom, useShortFrom } from "../shortFrom";
import { brandsOf, byLocationOf, formNameOf, rowsOf, totalOf, type Variety } from "./hopVarieties";

export type Inv = Layout["inventory"];
export function whereText(item: Item, locations: Location[], mode: "unit" | "pack"): string {
  const names = new Map(locations.map((l) => [l.id, l.name]));
  return item.onHand.byLocation
    .map((b) => `${names.get(b.locationId) ?? "?"} ${showBalance(item, b, mode)}`)
    .join(", ");
}
/** A variety's stock across its forms; a strain's form by form, since a pitch and a weight do not add up. */
export function varietyOnHand(v: Variety, mode: "unit" | "pack"): string {
  if (v.kind !== "yeast") {
    return formatQuantity(totalOf(v), { unit: v.items[0]?.displayUnit ?? "lb" });
  }
  return (
    v.items
      .filter((i) => !dec(i.onHand.quantity).eq(ZERO))
      .map((i) => showOnHand(i, mode))
      .join(", ") || showOnHand(v.items[0] as Item, mode)
  );
}
/** Where a variety's stock is, by location; a strain's form by form. */
export function varietyWhere(v: Variety, locations: Location[], mode: "unit" | "pack"): string {
  if (v.kind === "yeast") {
    return v.items
      .map((i) => whereText(i, locations, mode))
      .filter(Boolean)
      .join("; ");
  }
  const names = new Map(locations.map((l) => [l.id, l.name]));
  const unit = v.items[0]?.displayUnit ?? "lb";
  return [...byLocationOf(v)]
    .filter(([, q]) => !q.eq(ZERO))
    .map(([id, q]) => `${names.get(id) ?? "?"} ${formatQuantity(q, { unit })}`)
    .join(", ");
}
/**
 * Up while a category is open, to fold it; down while it is folded, to drop it back down. It sits at
 * the far right of the category's bar, clear of Expand all (Tyler, 2026-10-02).
 */
export const FoldCaret = ({ folded }: { folded: boolean }) =>
  folded ? (
    <ChevronDown size={16} className="fold-caret" aria-hidden />
  ) : (
    <ChevronUp size={16} className="fold-caret" aria-hidden />
  );
/**
 * A category's heading: its name and count, which fold it up and drop it down. Over cards and the
 * overview it spans the line with its caret at the end; the table's row carries its own caret.
 */
export function GroupToggle({
  category,
  count,
  folded,
  controls,
  caret = true,
  onToggle,
}: {
  category: string;
  count: number;
  folded: boolean;
  controls?: string;
  caret?: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className="group-toggle"
      style={catStyle(category)}
      aria-expanded={!folded}
      aria-controls={controls}
      onClick={(e) => {
        // The table's group row folds on a click too; this one click must not fold it twice.
        e.stopPropagation();
        onToggle();
      }}
    >
      <CategoryChip category={category} />
      <span className="count">{count}</span>
      {caret ? <FoldCaret folded={folded} /> : null}
    </button>
  );
}
export function Cards({
  inv,
  items,
  locations,
}: {
  inv: Inv;
  items: Item[];
  locations: Location[];
}) {
  return (
    <div className={`card-grid size-${inv.cardSize}`}>
      {rowsOf(items).map((r) => {
        if (r.kind === "variety") return varietyCard(r.variety);
        const i = r.item;
        return (
          <Link
            key={i.id}
            to="/items/$itemId"
            params={{ itemId: i.id }}
            className="stock-card"
            style={catStyle(i.category)}
          >
            <h3 title={i.displayName}>{i.displayName}</h3>
            <div className={`big num ${i.onHand.negative ? "negative" : ""}`}>
              {showOnHand(i, inv.quantityMode)}
            </div>
            {i.pack && inv.quantityMode === "unit" ? (
              <div className="sub num">{showOnHand(i, "pack")}</div>
            ) : null}
            {i.pack && inv.quantityMode === "pack" ? (
              <div className="sub num">{showOnHand(i, "unit")}</div>
            ) : null}
            <PlannedNote item={i} mode={inv.quantityMode} />
            <div className="where">
              {whereText(i, locations, inv.quantityMode) || "Not in stock"}
            </div>
            {/* The form shows on the smallest cards too: it tells Citra T-90 from Citra Cryo. */}
            {makerText(i) || formLabel(i) ? (
              <div className={formLabel(i) ? "sub" : "sub maker"}>
                {formLabel(i)}
                {makerText(i) ? (
                  <span className="maker">
                    {formLabel(i) ? " · " : ""}
                    {makerText(i)}
                  </span>
                ) : null}
              </div>
            ) : null}
          </Link>
        );
      })}
    </div>
  );

  /**
   * A hop variety on a phone: its total and where, and each form a link to its lots, so nothing needs
   * dropping down on a screen that small.
   */
  function varietyCard(v: Variety) {
    const where = varietyWhere(v, locations, inv.quantityMode);
    const total = varietyOnHand(v, inv.quantityMode);
    return (
      <div key={v.key} className="stock-card variety-card" style={catStyle(v.kind)}>
        <h3 title={v.name}>{v.name}</h3>
        <div className={`big num ${v.items.some((i) => i.onHand.negative) ? "negative" : ""}`}>
          {total}
        </div>
        <div className="where">{where || "Not in stock"}</div>
        <ul className="variety-forms">
          {v.items.map((i) => (
            <li key={i.id}>
              <Link to="/items/$itemId" params={{ itemId: i.id }}>
                {formNameOf(i)}
              </Link>
              <span className="num">{showOnHand(i, inv.quantityMode)}</span>
            </li>
          ))}
        </ul>
        {brandsOf(v) ? <div className="sub maker">{brandsOf(v)}</div> : null}
      </div>
    );
  }
}
/**
 * Available below zero, with the day it starts to bite: red when that is inside To order's window,
 * plain when it is further off (`useShortFrom`). Below zero with no day known stays red.
 */
export function Available({
  below,
  short,
  children,
}: {
  below: boolean;
  short: ShortFrom | null;
  children: ReactNode;
}) {
  return (
    <span className={`num ${below && (short?.soon ?? true) ? "negative" : ""}`}>
      {children}
      {short ? <span className="short-from">from {showDate(short.on)}</span> : null}
    </span>
  );
}
/** What the schedule plans of an item and what that leaves available, when it plans any. */
function PlannedNote({ item: i, mode }: { item: Item; mode: "unit" | "pack" }) {
  const shortFrom = useShortFrom();
  if (dec(i.planned).eq(ZERO)) return null;
  const below = dec(i.available).lt(ZERO);
  const short = below ? shortFrom(i.id) : null;
  return (
    <div className={`sub num ${below && (short?.soon ?? true) ? "negative" : ""}`}>
      {showQuantity(i, i.planned, mode)} planned,{" "}
      {showAvailable(i, i.available, mode, " available")}
      {short ? <span className="short-from inline"> from {showDate(short.on)}</span> : null}
    </div>
  );
}
export function Overview({
  inv,
  items,
  locations,
}: {
  inv: Inv;
  items: Item[];
  locations: Location[];
}) {
  const { layout } = useLayout();
  return (
    <div className="overview-list">
      {items.map((i) => (
        <Link
          key={i.id}
          to="/items/$itemId"
          params={{ itemId: i.id }}
          className="overview-row"
          style={catStyle(i.category)}
        >
          <span className="stripe" />
          <div>
            <h3>{i.displayName}</h3>
            <div className="muted">
              {[categoryLabel(i.category), formLabel(i), makerText(i), regionText(i)]
                .filter(Boolean)
                .join(" · ")}
            </div>
            {i.specs.length ? (
              <div className="specs">
                {i.specs
                  .filter((s) => s.kind === "numeric")
                  .slice(0, 5)
                  .map((s) => (
                    <span key={s.property}>
                      {s.label} {showSpec(s, layout.preferences.colorUnit).text}
                    </span>
                  ))}
              </div>
            ) : null}
          </div>
          <div className="right">
            <div className={`big num ${i.onHand.negative ? "negative" : ""}`}>
              {showOnHand(i, inv.quantityMode)}
            </div>
            <PlannedNote item={i} mode={inv.quantityMode} />
            <div className="muted">
              {whereText(i, locations, inv.quantityMode) || "Not in stock"}
            </div>
            {i.cost.averageOnHand ? (
              <div className="muted num">
                {showUnitCost(i, i.cost.averageOnHand, inv.quantityMode)} average
              </div>
            ) : null}
            <div className="faint">{relative(i.lastMovementAt)}</div>
          </div>
        </Link>
      ))}
    </div>
  );
}
